// src/components/workspace/WorkspaceControls.tsx
// A list page's row in the navbar: which view draws the list, what is
// narrowing it, one door to everything it can be set to, and the one thing
// you came to do.
//
// The view switcher and search are everyday navigation, so they stay on
// screen: switching between list and board is a glance-level action, not a
// setting. Grouping, sorting, density and scope are edits you make once and
// then read the result of — those fold into the settings menu. Active
// filters stay visible as chips, because a filter you cannot see is a list
// quietly missing rows. Create is the reason the page exists, and a primary
// action behind two clicks is a primary action in name only.

import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

import { FilterChips } from '@/components/workspace/FilterChips';
import { SettingsMenu, SettingsRootOnly } from '@/components/workspace/SettingsSurface';
import { ViewOptions, activeNarrowingCount } from '@/components/workspace/ViewOptions';
import { useWorkspace } from '@/features/workspace/context';

import type { ViewType } from '@/features/workspace/types';

const VIEW_ICONS: Record<string, string> = {
  list: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  board: 'M9 4h6v16H9zM3 4h6v10H3zM15 4h6v12h-6z',
  timeline: 'M3 12h4l3-9 4 18 3-9h4',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  schedule: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  tree: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
  grid: 'M4 5h6v6H4zM14 5h6v6H4zM4 15h6v6H4zM14 15h6v6H4z',
  table: 'M3 10h18M3 14h18M3 18h18M3 6h18',
};

function ViewIcon({ icon, className }: { icon: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d={VIEW_ICONS[icon] || VIEW_ICONS.list} />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// View switcher — the workspace's primary navigation, always visible
// ---------------------------------------------------------------------------

const ViewSwitcher = observer(() => {
  const { t } = useI18n();
  const { state, config, dispatch } = useWorkspace();
  const views = config.availableViews.filter((v) => v.available);
  if (views.length <= 1) return null;

  return (
    <div
      role="tablist"
      aria-label={t('workspace.views.label')}
      className="flex flex-shrink-0 items-center rounded-md border border-border bg-muted/50 p-0.5"
    >
      {views.map((view) => {
        const active = state.view === view.id;
        return (
          <button
            key={view.id}
            type="button"
            role="tab"
            aria-selected={active}
            title={t(view.labelKey)}
            onClick={() => dispatch({ type: 'SET_VIEW', view: view.id as ViewType })}
            className={cn(
              'flex h-6 w-7 items-center justify-center rounded transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <ViewIcon icon={view.icon} className="h-3.5 w-3.5" />
            <span className="sr-only">{t(view.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Inline search — one keystroke away, not buried in a menu
// ---------------------------------------------------------------------------

const WorkspaceSearch = observer(() => {
  const { t } = useI18n();
  const { searchDraft, onSearchDraftChange } = useWorkspace();

  return (
    <div className="relative hidden md:block">
      <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={searchDraft}
        onChange={(e) => onSearchDraftChange(e.target.value)}
        placeholder={t('workspace.search.placeholder')}
        aria-label={t('workspace.search.placeholder')}
        className={cn(
          'h-7 w-36 rounded-md border border-transparent bg-transparent pl-7 pr-2 text-xs text-foreground',
          'placeholder:text-muted-foreground transition-all',
          'hover:border-border focus:w-52 focus:border-border focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring',
          searchDraft.trim() && 'border-border bg-background w-52',
        )}
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Controls row
// ---------------------------------------------------------------------------

export interface WorkspaceControlsProps {
  /** The page's own sections ("Teams | Invitations"), as a segmented control. */
  sections?: React.ReactNode;
  /** What the list is drawn from, as a <SettingsSection>. */
  scope?: React.ReactNode;
  /** The saved views, as a <SettingsSection>. */
  savedViews?: React.ReactNode;
  /** The saved views, as a compact navbar dropdown for one-click applying. */
  savedViewsMenu?: React.ReactNode;
  createLabel?: string;
  onCreateClick?: () => void;
}

export const WorkspaceControls = observer(({
  sections,
  scope,
  savedViews,
  savedViewsMenu,
  createLabel,
  onCreateClick,
}: WorkspaceControlsProps) => {
  const { t } = useI18n();
  const { state, searchDraft } = useWorkspace();
  const badge = activeNarrowingCount(state.filters.length, searchDraft);

  return (
    <>
      {savedViewsMenu}
      <ViewSwitcher />
      <WorkspaceSearch />
      <FilterChips />

      <SettingsMenu ariaLabel={t('workspace.pageSettings')} badge={badge}>
        {/* The page's own things first: which part of the page you are on, and
            what the list is drawn from, come before how it is drawn. */}
        {sections && (
          <SettingsRootOnly>
            <div className="p-1">{sections}</div>
          </SettingsRootOnly>
        )}
        {scope}
        {savedViews}
        <ViewOptions />
      </SettingsMenu>

      {onCreateClick && createLabel && (
        <button
          type="button"
          onClick={onCreateClick}
          aria-label={t(createLabel)}
          title={t(createLabel)}
          className="flex h-7 flex-shrink-0 items-center gap-1 rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <PlusIcon className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="hidden sm:inline">{t(createLabel)}</span>
        </button>
      )}
    </>
  );
});

WorkspaceControls.displayName = 'WorkspaceControls';
